import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Grid,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  TextField,
  Typography,
} from "@material-ui/core";
import { alpha } from "@material-ui/core/styles/colorManipulator";

import AddIcon from "@material-ui/icons/AddOutlined";

import CloseIcon from "@material-ui/icons/Close";
import EditIcon from "@material-ui/icons/Code";
import SearchIcon from "@material-ui/icons/SearchOutlined";
import { makeStyles } from "@material-ui/styles";
import * as Sentry from "@sentry/browser";
import clsx from "clsx";
import firebase from "firebase/app";
import _ from "lodash";
import { highlight, languages } from "prismjs/components/prism-core";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useState } from "react";
import { useAlert } from "react-alert";
import Editor from "react-simple-code-editor";
import useInterval from "react-use/lib/useInterval";
import useLocalStorage from "react-use/lib/useLocalStorage";

import AnimateHeight from "react-animate-height";

import "react-datetime/css/react-datetime.css";
import { DownloadIcon } from "../../../components/icons/themes/materialdesign";

import AutocompletePropField from "../../../AutocompletePropField";

import { RefreshIcon } from "../../../components/icons/themes/windows10";
import {
  destinationKeyToSlugMap,
  warehouseDestinations,
} from "../../../destinations/destinations";
import {
  allEventTypes,
  eventCategories,
  eventDefType,
  libraryForEventType,
} from "../../../eventTypes";
import FilterList from "../../../FilterList";
import useCurrentEnvironment from "../../../hooks/use-environment";
import { useAllSaasDestinations } from "../../../hooks/useAllSaasDestinations";
import { useDestinationMetadataManager } from "../../../hooks/useDestinationMetadataManager";
import "../../../theme/custom.css";
import {
  iconForEventType,
  isNetworkFilter,
  makeRequest,
  getFeatureFlag,
} from "../../../util";
import { ChangeNetworkEventModal } from "./ChangeNetworkEventModal";

import { CodeEditorModal } from "../../../CodeEditorModal";
import eventsEmptyStateImage from "../../../img/events_empty_state.svg";
import useFeatureFlags from "../../../hooks/useFeatureFlag";

const useTopbarStyles = makeStyles((theme) => ({
  topbar: {
    display: "flex",
    backgroundColor: "transparent",
    width: "100%",
    flex: "0 1 auto",
    height: 70,
  },
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  searchWrapper: {
    width: 275,
    borderBottom: "2px solid rgb(63 63 68 / 15%)",
  },
  searchField: {},
  exportEventButton: {
    position: "absolute",
    right: 0,
    top: 40,
    width: 180,
    height: 45,
    backgroundColor: "white",
    color: "#3f51b5",
    border: `1px solid ${alpha("#3f51b5", 0.5)}`,
    "&:hover": {
      backgroundColor: "#f5f5f5",
      border: `1px solid #3f51b5`,
    },
  },
  exportEventButtonWrapper: {
    position: "relative",
  },
}));

let EventsTopbar = ({
  searchString,
  onSearchStringChange,
  onNewEventDef,
  editorState,
  downloadEventSchema,
}) => {
  const classes = useTopbarStyles();

  const matchingCategory = _.find(
    eventCategories,
    (cat) => cat.type === editorState.type
  );

  const headline = matchingCategory
    ? `${matchingCategory.name} / ${editorState.name}`
    : editorState.name;

  return (
    <div className={clsx(classes.topbar)}>
      <div
        className={clsx(
          "bg-white p-4 flex justify-center items-center MuiPaper-elevation1",
          classes.searchWrapper
        )}
      >
        <TextField
          className="w-full"
          variant="outlined"
          name="search"
          autoComplete="off"
          onChange={(e) => onSearchStringChange(e.target.value)}
          value={searchString}
          InputProps={{
            className: clsx(classes.searchField),
            placeholder: "Search events",
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon className={clsx(classes.icon)} />
              </InputAdornment>
            ),
          }}
        />
      </div>

      <div
        className={clsx(
          "px-5 py-4 items-center truncate font-bold text-gray-700 flex-grow"
        )}
      >
        <span className="text-2xl">{headline}</span>
      </div>

      <div className={clsx("p-3 flex justify-center items-center")}>
        <Button
          className="h-full"
          color="primary"
          onClick={() => onNewEventDef()}
          variant="contained"
        >
          <AddIcon className={classes.icon} />
          Create New Event
        </Button>
        <div className={clsx(classes.exportEventButtonWrapper)}>
          <Button
            className={clsx(classes.exportEventButton)}
            color="primary"
            onClick={() => downloadEventSchema()}
            variant="contained"
          >
            <DownloadIcon className={classes.icon} />
            Export Events
          </Button>
        </div>
      </div>
    </div>
  );
};

EventsTopbar.propTypes = {
  searchString: PropTypes.string,
  onSearchStringChange: PropTypes.func,
  onNewEventDef: PropTypes.func,
  editorState: PropTypes.any,
};
EventsTopbar = React.memo(EventsTopbar);

export function EventsDrawer({ event, onClose }) {}

const useEventListStyles = makeStyles((theme) => ({
  eventListPaper: {
    height: "100%",
    width: 275,
    overflowY: "auto",
    flexShrink: 0,
    borderRadius: 0,
  },
  eventList: {
    whiteSpace: "nowrap",
    overflow: "hidden",
  },
  eventTypeHeader: {
    backgroundColor: "white",
    color: "#617984",
  },
  headerText: {
    "& span": {
      color: "#617984",
      fontWeight: "bold",
      textTransform: "uppercase",
      fontSize: 12,
    },
  },
  triangle: {
    borderTop: "4px solid #4050B5",
    borderLeft: "4px solid transparent",
    borderRight: "4px solid transparent",
    width: 0,
    height: 0,
    transition: ".5s all",
  },
  triangleOpen: {
    transform: "rotate(180deg)",
  },
  listItem: {
    borderBottom: "1px solid #eeeeee",
    overflow: "hidden",
    transition: "flex .5s ease-in-out",
  },
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
    fill: "#617984",
  },
  active: {
    backgroundColor: "#C5CAE9",
    fontWeight: 550,
    "&:hover": {
      backgroundColor: "#C5CAE9",
    },
  },
  hidden: {
    visibility: "hidden",
  },
}));

// Renders the list of events. We do the string searching inside of EventList so
// React.memo will work. If the events object changes every call, React.memo
// will rerender this component.
const EventList = React.memo(function EventList({
  activeEvent,
  events,
  onSelect,
  searchString,
}) {
  const classes = useEventListStyles();

  events =
    events &&
    events.filter((event) =>
      event.name.toLowerCase().includes(searchString.toLowerCase())
    );

  const invisibleEvents = events
    ? events.filter((event) => !allEventTypes.includes(eventDefType(event)))
    : [];
  for (var i = 0; i < invisibleEvents.length; i++) {
    Sentry.captureException(
      new Error(`Event ${invisibleEvents[i].id} not showing in UI`)
    );
  }

  const hiddenCategory = { name: "hidden", type: "hidden" };
  const renderedCategories = eventCategories
    .concat([hiddenCategory])
    .map((category) => {
      return (
        <EventListCategory
          key={category.name}
          name={category.name}
          events={events}
          type={category.type}
          activeEvent={activeEvent}
          onSelect={onSelect}
        />
      );
    });

  return (
    <Paper
      className={clsx(
        classes.eventListPaper,
        (!events || events.length === 0) && classes.hidden
      )}
    >
      <List disablePadding className={clsx(classes.eventList)}>
        {renderedCategories}
      </List>
    </Paper>
  );
});

EventList.propTypes = {
  activeEvent: PropTypes.string,
  events: PropTypes.array,
  onSelect: PropTypes.func,
  searchString: PropTypes.string,
};

// EventListCategory renders an event list for one category of event.
function EventListCategory({ name, events, type, activeEvent, onSelect }) {
  const classes = useEventListStyles();
  const Icon = iconForEventType(type);

  const [isOpen, setIsOpen] = useLocalStorage(
    `events/shema/category/${type}/`,
    true
  );

  events =
    events &&
    events.filter((event) =>
      event.is_hidden ? type === "hidden" : eventDefType(event) === type
    );

  if (_.isEmpty(events)) {
    return null;
  }

  return (
    <>
      <ListItem
        divider
        className={clsx(classes.eventTypeHeader, "cursor-pointer")}
        onClick={() => setIsOpen(!isOpen)}
      >
        {Icon && <Icon height={20} width={20} className={classes.icon} />}
        <ListItemText
          classes={{ root: clsx(classes.headerText) }}
          primary={name}
        />
        <div
          className={clsx(classes.triangle, isOpen && classes.triangleOpen)}
        />
      </ListItem>
      <AnimateHeight duration={500} height={isOpen ? "auto" : 0}>
        {events.map((event) => {
          return (
            <ListItem
              className={clsx(
                classes.listItem,
                activeEvent === event.id && classes.active
              )}
              key={event.id}
              button
              onClick={() => onSelect(event)}
            >
              <ListItemText
                primary={event.name}
                className="truncate"
                title={event.name}
              />
            </ListItem>
          );
        })}
      </AnimateHeight>
    </>
  );
}

EventListCategory.propTypes = {
  name: PropTypes.string.isRequired,
  events: PropTypes.array,
  type: PropTypes.string.isRequired,
  activeEvent: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

const useEventEditorStyles = makeStyles((theme) => ({
  icon: {
    display: "flex",
    marginRight: theme.spacing(1),
  },
  filterRegion: {
    display: "flex",
    justifyContent: "flex-end",
    alignContent: "center",
  },
  createEventButton: {
    color: theme.palette.common.white,
    backgroundColor: colors.green[600],
    cursor: "pointer",
    "&:hover": {
      backgroundColor: colors.green[900],
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: colors.green[600],
      },
    },
    "&:disabled": {
      visibility: "hidden",
      cursor: "not-allowed",
    },
  },
  deleteEventButton: {
    color: colors.red[600],
    border: `1px solid ${alpha(colors.red[600], 0.5)}`,
    "&:hover": {
      border: `1px solid ${colors.red[600]}`,
      backgroundColor: alpha(
        colors.red[600],
        theme.palette.action.hoverOpacity
      ),
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: "transparent",
      },
    },
  },
  eventActions: {
    display: "flex",
    justifyContent: "space-between",
  },
  hidden: {
    visibility: "hidden",
  },
}));

function shallowEqual(a, b) {
  return _.isEqualWith(a, b, (x, y) => {
    if (!_.isObject(x) && !_.isObject(y)) {
      return x === y;
    }

    return undefined;
  });
}

const MemoTextField = React.memo(TextField, (a, b) => {
  return shallowEqual(_.omit(a, "children"), _.omit(b, "children"));
});

export function CustomEventDisplay({
  editorState,
  setEventHidden,
  setEditorState,
  onSave,
}) {
  const [hasChanges, setHasChanges] = useState(false);
  const classes = useEventEditorStyles();

  const handlePropertyChange = useCallback(
    function handlePropertyChange(prop, value) {
      setEditorState((editorState) => {
        const newEditorState = _.extend({}, editorState, { [prop]: value });
        return newEditorState;
      });
    },
    [setEditorState]
  );

  const setDescription = useCallback(
    function setName(e) {
      if (!hasChanges) {
        setHasChanges(true);
      }

      handlePropertyChange("description", e.target.value);
    },
    [handlePropertyChange, hasChanges]
  );

  return (
    <Card>
      <CardHeader
        title="Event Name"
        action={
          <div aria-label="info" className="text-xs text-grey-600">
            {editorState.created_at && (
              <>
                <span className="font-bold mr-3">Created:</span>
                {new Date(editorState.created_at).toLocaleString()}
                {editorState.created_by && (
                  <>
                    &nbsp;by&nbsp;
                    {editorState.created_by}
                  </>
                )}
                <br />
              </>
            )}
            {editorState.updated_at && (
              <>
                <span className="font-bold mr-2">Updated:</span>
                {new Date(editorState.updated_at).toLocaleString()}
                {editorState.last_updated_by && (
                  <>
                    &nbsp;by&nbsp;
                    {editorState.last_updated_by}
                  </>
                )}
              </>
            )}
          </div>
        }
      />
      <Divider />
      <CardContent>
        <div style={{ paddingLeft: "15px" }}>
          <Typography variant="h5">{editorState.name}</Typography>
        </div>
      </CardContent>
      <Divider />
      <CardContent>
        <Grid container>
          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              multiline
              minRows={2}
              maxRows={5}
              label="Description (optional)"
              placeholder="Add a description for this event"
              name="description"
              onChange={setDescription}
              value={editorState.description}
            />
          </Grid>
        </Grid>

        <Grid container style={{ marginTop: 16 }}>
          <Button
            className={clsx(classes.deleteEventButton)}
            onClick={() => setEventHidden(!editorState.is_hidden)}
          >
            {editorState.is_hidden ? "Restore Event" : "Hide Event"}
          </Button>
          <Button
            style={{ marginLeft: "auto" }}
            className={classes.createEventButton}
            onClick={onSave}
            variant="outlined"
            disabled={!hasChanges}
          >
            Save
          </Button>
        </Grid>
      </CardContent>
    </Card>
  );
}

CustomEventDisplay.propTypes = {
  editorState: PropTypes.object.isRequired,
  setEventHidden: PropTypes.func.isRequired,
  setEditorState: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
};

// This is there is only a single instance of this object. This allows us to use
// memoization for the MemoTextField when passing in the shrink argument.
const shrink = { shrink: true };
const native = { native: true };

export function EventEditor({
  screenshot,
  editorState,
  editorType,
  unsavedEdits,
  errorMessage,
  setErrorMessage,
  setState,
  setScreenshot,
  onSave,
  onDelete,
  hasNetworkEventEditPerms,
  isNetworkDynProp,
  hasJsDynPropEditPerms,
  destinations,
}) {
  const alert = useAlert();
  const classes = useEventEditorStyles();
  const flag = useFeatureFlags();
  const currentEnvironment = useCurrentEnvironment();
  const [changeNwEventModalOpen, setChangeNwEventModalOpen] = useState(false);
  const [changeNwEventNewType, setChangeNwEventNewType] = useState({});
  const allSaasDestinations = useAllSaasDestinations();
  const allDestinations = { ...warehouseDestinations, ...allSaasDestinations };
  const destinationMetadataManager = useDestinationMetadataManager();
  const library = libraryForEventType(editorState.type);
  const validGADestinations = destinations.filter((dest) => {
    const destinationSlug =
      destinationKeyToSlugMap[dest.name] || dest.function_id;
    const destinationDefinition = allDestinations[destinationSlug];
    return (
      dest.enabled &&
      dest.name.startsWith("Google Analytics") &&
      destinationMetadataManager.destinationIsValidForEvent(
        dest,
        editorState.type
      ) &&
      (!destinationDefinition.supportedSources ||
        destinationDefinition.supportedSources[library])
    );
  });
  let nameHelperText = "";
  if (editorState.name.length > 40 && validGADestinations.length > 0) {
    nameHelperText =
      "Note: Google Analytics will truncate the Event Name at 40 characters";
  }

  const handlePropertyChange = useCallback(
    function handlePropertyChange(prop, value) {
      setState((editorState) => {
        const newEditorState = _.extend({}, editorState, { [prop]: value });
        return newEditorState;
      });
    },
    [setState]
  );

  const setName = useCallback(
    function setName(e) {
      handlePropertyChange("name", e.target.value);
    },
    [handlePropertyChange]
  );

  const setDescription = useCallback(
    function setName(e) {
      handlePropertyChange("description", e.target.value);
    },
    [handlePropertyChange]
  );

  const setType = useCallback(
    function setType(e) {
      setErrorMessage("");
      setChangeNwEventNewType({});
      if (editorState.type === "network" && e.target.value !== "network") {
        let confirmNeeded = false;
        for (let uiFilter of editorState.filters) {
          if (isNetworkFilter(uiFilter)) {
            confirmNeeded = true;
          }
        }
        for (let snapshot of editorState.snapshots) {
          if (isNetworkDynProp(snapshot)) {
            confirmNeeded = true;
          }
        }
        if (confirmNeeded) {
          changeNwEventNewType.type = e.target.value;
          setChangeNwEventModalOpen(true);
          return;
        }
      }
      handlePropertyChange("type", e.target.value);
    },
    [
      setErrorMessage,
      handlePropertyChange,
      changeNwEventNewType.type,
      editorState.filters,
      editorState.snapshots,
      editorState.type,
      isNetworkDynProp,
    ]
  );

  const handleChangeNwEvent = useCallback(
    function handleChangeNetworkEvent() {
      //Delete network-specific filters and dyn props

      setState((editorState) => {
        let newFilters = [];
        for (let uiFilter of editorState.filters) {
          if (!isNetworkFilter(uiFilter)) {
            newFilters.push(uiFilter);
          }
        }
        let newSnapshots = [];
        for (let snapshot of editorState.snapshots) {
          if (!isNetworkDynProp(snapshot)) {
            newSnapshots.push(snapshot);
          }
        }
        const newState = _.extend({}, editorState, {
          type: changeNwEventNewType.type,
          filters: newFilters,
          snapshots: newSnapshots,
        });

        return newState;
      });
    },
    [changeNwEventNewType.type, setState, isNetworkDynProp]
  );

  function handleChangeNwEventModalCancel() {
    setChangeNwEventModalOpen(false);
  }

  const setTypeSpecificFilterVal = useCallback(
    function setTypeSpecificFilterVal(value) {
      handlePropertyChange("typeSpecificFilterVal", value);
    },
    [handlePropertyChange]
  );

  const setLibrary = useCallback(
    function setLibrary(e) {
      // We determine the library in use based on the event type. When you toggle
      // a new library, we just change the current event type to one from the
      // designated library.
      setErrorMessage("");
      if (e.target.value === "web") {
        handlePropertyChange("type", "click");
      } else if (e.target.value === "react-native") {
        handlePropertyChange("type", "touch");
      } else if (e.target.value === "sql") {
        handlePropertyChange("type", "scheduled_event");
      }
    },
    [setErrorMessage, handlePropertyChange]
  );

  function handleAddFilter() {
    setState((editorState) => {
      const newFilter = {
        type: "prop_filter",
        prop: "",
        nw_path: "",
        op: "",
        val: "",
      };

      const newFilters = editorState.filters.concat([newFilter]);
      const newState = _.extend({}, editorState, { filters: newFilters });
      return newState;
    });
  }

  function handleAddSnapshot() {
    setState((editorState) => {
      const newSnapshot = {
        name: "",
        type: "css_selector",
        value: "",
      };

      const newSnapshots = editorState.snapshots.concat([newSnapshot]);
      const newState = _.extend({}, editorState, { snapshots: newSnapshots });
      return newState;
    });
  }

  // optionsForLibrary returns the list of option components for the event type
  // dropdown.
  function optionsForLibrary(library) {
    if (library === "web") {
      return (
        <>
          <option value="click">Click</option>
          <option value="pageview">Pageview</option>
          <option value="submit">Form Submit</option>
          <option value="change">Change</option>
          {hasNetworkEventEditPerms() && (
            <>
              <option value="network">Network</option>
            </>
          )}
        </>
      );
    } else if (library === "react-native") {
      return (
        <>
          <option value="touch">Touch</option>
          <option value="text_edit">Edit Input</option>
          <option value="toggle">Toggle Switch</option>
          <option value="react_navigation_screenview">Screen View</option>
        </>
      );
    } else if (library === "sql") {
      return (
        <>
          <option value="scheduled_event">Scheduled Event</option>;
          <option value="scheduled_identify">Scheduled Identify</option>;
        </>
      );
    } else {
      return <></>;
    }
  }

  const setFilterListState = useCallback(
    function setFilterListState(updateFiltersFn) {
      setState((editorState) => {
        const newFilters = updateFiltersFn(editorState.filters);
        const newState = _.extend({}, editorState, { filters: newFilters });
        return newState;
      });
    },
    [setState]
  );

  const setSnapshotListState = useCallback(
    function setSnapshotListState(updateSnapshotsFn) {
      setState((editorState) => {
        const newSnapshots = updateSnapshotsFn(editorState.snapshots);
        const newState = _.extend({}, editorState, { snapshots: newSnapshots });
        return newState;
      });
    },
    [setState]
  );

  var typeSpecificFilterName;
  var typeSpecificFilterProp;
  var placeholderText;

  if (editorState.type === "pageview") {
    typeSpecificFilterName = "URL Path";
    typeSpecificFilterProp = "$pathname";
    placeholderText = "For example /about or /products/*";
  } else if (editorState.type === "react_navigation_screenview") {
    typeSpecificFilterName = "Screen View Route";
    typeSpecificFilterProp = "$screen_path";
  } else if (editorState.type === "network") {
    typeSpecificFilterName = "URL Path";
    typeSpecificFilterProp = "$pathname";
    placeholderText = "For example /item or /item/*";
  } else {
    typeSpecificFilterName = "CSS Selector";
    typeSpecificFilterProp = null;
  }

  const proteusEnabled =
    !currentEnvironment.loaded ||
    !!getFeatureFlag(currentEnvironment.project, "sqlTransformations");

  useEffect(() => {
    if (libraryForEventType(editorState.type) === "sql" && !proteusEnabled) {
      setErrorMessage(
        `Contact support@freshpaint.io to enable this feature for the ${currentEnvironment.project.name} project. Provide your project ID: ${currentEnvironment.project.ID}.`
      );
    }
  }, [editorState.type, setErrorMessage, currentEnvironment, proteusEnabled]);

  const [isSyncing, setSyncing] = useState(false);
  // syncStatus is the latest result of the batch event definition sync. This is
  // refreshed regularly by polling.
  const [syncStatus, setSyncStatus] = useState();

  // Lazily grab the initial sync status
  useEffect(() => {
    if (
      editorType === "create" ||
      libraryForEventType(editorState.type) !== "sql" ||
      !proteusEnabled
    ) {
      return;
    }
    if (syncStatus) {
      return;
    }
    makeRequest("get_batch_event_def_sync_status", {
      event_def_id: editorState.id,
    }).then((syncStatusResp) => {
      setSyncing(syncStatusResp.data.lock_value != "");
      setSyncStatus((status) => {
        if (status) {
          return status;
        }
        return syncStatusResp.data;
      });
    });
  });

  // Poll every 10 seconds to update the sync status
  useInterval(() => {
    if (
      editorType === "create" ||
      libraryForEventType(editorState.type) !== "sql" ||
      !proteusEnabled
    ) {
      return;
    }
    makeRequest("get_batch_event_def_sync_status", {
      event_def_id: editorState.id,
    }).then((syncStatusResp) => {
      setSyncing(syncStatusResp.data.lock_value !== "");
      if (
        !syncStatus ||
        syncStatusResp.data.last_completed_at !== syncStatus.last_completed_at
      ) {
        setSyncStatus(syncStatusResp.data);
        alert.success(
          "Scheduled Event Definition Sync completed: " +
            getSyncStatusString(syncStatusResp.data)
        );
      }
    });
  }, 10000);

  function onSyncNow() {
    if (unsavedEdits) {
      alert.error("You have unsaved event definition changes.");
      return;
    }

    setSyncing(true);
    makeRequest("sync_batch_event_def", { event_def_id: editorState.id })
      .then((syncResponse) => {
        if (syncResponse.data.error_message) {
          // This tells the user if a sync was already in progress
          alert.success(syncResponse.data.error_message);
        } else {
          alert.success(
            "Scheduled Event Definition Sync initiated. This may take several minutes to complete."
          );
        }
      })
      .catch((error) => {
        setSyncing(false);
        alert.error("Something went wrong. Unable to sync event definition.");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  function handleDeleteScreenshot() {
    makeRequest("delete_event_def_screenshot", { event_def_id: editorState.id })
      .then((resp) => {
        setScreenshot("");
      })
      .catch((error) => {
        alert.error("Unable to remove screenshot");
        console.log(error);
        Sentry.captureException(error);
      });
  }

  return (
    <Card>
      <CardHeader
        title={
          editorType === "create"
            ? "Create A New Event"
            : "Edit Event" + (unsavedEdits ? " (Unsaved Changes)" : "")
        }
        action={
          <div aria-label="info" className="text-xs text-grey-600">
            {editorState.created_at && (
              <>
                <span className="font-bold mr-3">Created:</span>
                {new Date(editorState.created_at).toLocaleString()}
                {editorState.created_by && (
                  <>
                    &nbsp;by&nbsp;
                    {editorState.created_by}
                  </>
                )}
                <br />
              </>
            )}
            {editorState.updated_at && (
              <>
                <span className="font-bold mr-2">Updated:</span>
                {new Date(editorState.updated_at).toLocaleString()}
                {editorState.last_updated_by && (
                  <>
                    &nbsp;by&nbsp;
                    {editorState.last_updated_by}
                  </>
                )}
              </>
            )}
          </div>
        }
      />
      <Divider />
      <CardContent>
        <Grid container spacing={4}>
          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              autoFocus={editorType === "create"}
              InputLabelProps={shrink}
              variant="outlined"
              label="Event Name"
              helperText={nameHelperText}
              name="name"
              onChange={setName}
              value={editorState.name}
            />
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              label="Library"
              name="library"
              onChange={setLibrary}
              select
              SelectProps={native}
              value={libraryForEventType(editorState.type)}
              variant="outlined"
            >
              <option value="web">Web</option>
              <option value="react-native">React Native</option>
              {flag("sqlTransformations") && <option value="sql">SQL</option>}
            </MemoTextField>
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              label="Event Type"
              name="event-type"
              onChange={setType}
              select
              SelectProps={native}
              value={editorState.type}
              variant="outlined"
            >
              {optionsForLibrary(libraryForEventType(editorState.type))}
            </MemoTextField>
          </Grid>

          <Grid item xs={12}>
            {libraryForEventType(editorState.type) === "sql" ? (
              <Editor
                className="border-0 bg-gray-100 px-2 py-3 text-gray-900 mb-4 font-mono text-xs"
                padding={12}
                style={{ minHeight: 120 }}
                onValueChange={setTypeSpecificFilterVal}
                value={editorState.typeSpecificFilterVal || ""}
                placeholder="SELECT insert_id, user_id FROM events..."
                highlight={(code) => highlight(code, languages.sql)}
              />
            ) : (
              <AutocompletePropField
                propName={typeSpecificFilterProp}
                fullWidth
                autoComplete="off"
                variant="outlined"
                InputLabelProps={shrink}
                label={typeSpecificFilterName}
                placeholder={placeholderText}
                name="type-specific-filter-val"
                onChange={setTypeSpecificFilterVal}
                value={editorState.typeSpecificFilterVal}
              />
            )}
            {errorMessage && (
              <p className="text-xs text-red-600">{errorMessage}</p>
            )}
          </Grid>

          <Grid item xs={12}>
            <MemoTextField
              fullWidth
              autoComplete="off"
              InputLabelProps={shrink}
              variant="outlined"
              multiline
              minRows={2}
              maxRows={5}
              label="Description (optional)"
              placeholder="Add a description for this event"
              name="description"
              onChange={setDescription}
              value={editorState.description}
            />
          </Grid>
        </Grid>
      </CardContent>
      <Divider />
      {libraryForEventType(editorState.type) !== "sql" && (
        <>
          <CardContent>
            <FilterList
              filters={editorState.filters}
              setState={setFilterListState}
              library={libraryForEventType(editorState.type)}
              eventType={editorState.type}
            />
            <div className={classes.filterRegion}>
              <Button
                color="default"
                onClick={handleAddFilter}
                variant="outlined"
              >
                <AddIcon className={classes.icon} />
                Add Filter
              </Button>
            </div>
          </CardContent>
          <Divider />
          <CardContent>
            <SnapshotList
              eventType={editorState.type}
              snapshots={editorState.snapshots}
              setState={setSnapshotListState}
              isNetworkDynProp={isNetworkDynProp}
              hasNetworkEventEditPerms={hasNetworkEventEditPerms}
              hasJsDynPropEditPerms={hasJsDynPropEditPerms}
            />
            <div className={classes.filterRegion}>
              <Button
                color="default"
                onClick={handleAddSnapshot}
                variant="outlined"
              >
                <AddIcon className={classes.icon} />
                Add Dynamic Property
              </Button>
            </div>
          </CardContent>
        </>
      )}
      <Divider />
      <CardContent className={classes.eventActions}>
        <Button
          className={clsx(
            classes.deleteEventButton,
            editorType === "create" && classes.hidden
          )}
          onClick={onDelete}
        >
          Remove Event
        </Button>

        <Button
          className={classes.createEventButton}
          onClick={onSave}
          variant="outlined"
          disabled={
            libraryForEventType(editorState.type) === "sql" && !proteusEnabled
          }
        >
          {editorType === "create" ? "Create Event" : "Save"}
        </Button>
      </CardContent>
      <ChangeNetworkEventModal
        open={changeNwEventModalOpen}
        onClose={handleChangeNwEventModalCancel}
        onConfirm={handleChangeNwEvent}
      />
      {screenshot && (
        <>
          <Divider />
          <CardHeader
            title="Screenshot Preview"
            style={{ "padding-bottom": 0 }}
          />
          <CardContent>
            <img src={screenshot} />

            <Button
              className={clsx(classes.deleteEventButton)}
              onClick={handleDeleteScreenshot}
              style={{ marginTop: "15px" }}
            >
              Remove Screenshot
            </Button>
          </CardContent>
        </>
      )}
      {editorType !== "create" &&
        libraryForEventType(editorState.type) === "sql" && (
          <>
            <Divider />
            <CardContent className={classes.eventActions + " items-center"}>
              {syncStatus ? (
                <Typography variant="body1">
                  {getSyncStatusString(syncStatus)}
                </Typography>
              ) : (
                <div className="px-3 py-2 font-bold text-lg flex items-center rounded">
                  <RefreshIcon className="animate-spin mx-auto" />
                </div>
              )}
              {isSyncing && (
                <div className="px-3 py-2 font-bold text-lg flex items-center rounded">
                  <RefreshIcon className="animate-spin mx-auto" />
                </div>
              )}
              <Button
                color="primary"
                variant="outlined"
                onClick={onSyncNow}
                disabled={isSyncing || !proteusEnabled}
              >
                {isSyncing
                  ? "Sync In Progress"
                  : "Sync " + currentEnvironment.name + " Now"}
              </Button>
            </CardContent>
          </>
        )}
    </Card>
  );
}

EventEditor.propTypes = {
  editorState: PropTypes.object.isRequired,
  editorType: PropTypes.string.isRequired,
  unsavedEdits: PropTypes.bool.isRequired,
  errorMessage: PropTypes.string.isRequired,
  setErrorMessage: PropTypes.func.isRequired,
  setState: PropTypes.func.isRequired,
  setScreenshot: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

function SnapshotList({
  eventType,
  snapshots,
  setState,
  isNetworkDynProp,
  hasNetworkEventEditPerms,
  hasJsDynPropEditPerms,
}) {
  const handleSnapshotChange = useCallback(
    function handleSnapshotChange(index, snapshot) {
      setState((snapshots) => {
        const newList = snapshots.slice();
        newList[index] = snapshot;
        return newList;
      });
    },
    [setState]
  );

  const handleSnapshotDeletion = useCallback(
    function handleSnapshotDeletion(index) {
      setState((snapshots) => {
        const newSnapshots = snapshots.slice();
        newSnapshots.splice(index, 1);
        return newSnapshots;
      });
    },
    [setState]
  );

  if (typeof snapshots == "undefined") {
    snapshots = [];
  }
  const renderedSnapshots = snapshots.map((snapshot, index) => {
    return (
      <Snapshot
        eventType={eventType}
        key={index}
        index={index}
        snapshot={snapshot}
        onChange={handleSnapshotChange}
        onDelete={handleSnapshotDeletion}
        isNetworkDynProp={isNetworkDynProp}
        hasNetworkEventEditPerms={hasNetworkEventEditPerms}
        hasJsDynPropEditPerms={hasJsDynPropEditPerms}
      />
    );
  });

  return <>{renderedSnapshots}</>;
}

SnapshotList.propTypes = {
  snapshots: PropTypes.array.isRequired,
  setState: PropTypes.func.isRequired,
};

const snapshotStyles = makeStyles(() => ({
  snapshot: {
    marginBottom: 16,
  },
  closeIcon: {
    alignSelf: "center",
    "&:hover": {
      cursor: "pointer",
    },
  },
  bold: {
    fontWeight: 550,
  },
}));

const Snapshot = React.memo(function Snapshot({
  eventType,
  snapshot,
  index,
  onChange,
  onDelete,
  isNetworkDynProp,
  hasNetworkEventEditPerms,
  hasJsDynPropEditPerms,
}) {
  let codeEditorPrompt;
  if (snapshot.type === "js_code") {
    codeEditorPrompt =
      `Provide only a function body ending with a return statement.\n` +
      `\n` +
      ` This variable is available in your code:\n` +
      `\n` +
      `   var event; // The DOM event, if available (may be null)\n` +
      `\n` +
      ` Example:\n` +
      `   if (event.altKey) {\n` +
      `     return 'SUPPRESS_EVENT';\n` +
      `   }\n` +
      `   return ('x:' + event.clientX + ',y:' + event.clientY + ')';\n` +
      `\n` +
      ` Returning 'SUPPRESS_EVENT', as shown above, suppresses the event\n` +
      ` from being delivered to destinations.\n` +
      `\n`;
  } else if (snapshot.type === "css_selector") {
    codeEditorPrompt = `Enter a CSS Selector.\n\n`;
  } else {
    codeEditorPrompt =
      `Enter a dot-notation path.\n` +
      `\n` +
      ` Examples:\n` +
      `   .$request_headers.x-reddit-session\n` +
      `   .$response.purch_info.amt\n` +
      `   .$query_string_params.region\n` +
      `\n`;
  }

  const classes = snapshotStyles();
  const alert = useAlert();

  const [codeEditorModalOpen, setCodeEditorModalOpen] = useState(false);

  function handleCodeEditorModalSave(value) {
    handlePropertyChange("value", value);
    setCodeEditorModalOpen(false);
  }

  function handleCodeEditorModalCancel() {
    setCodeEditorModalOpen(false);
  }

  function handlePropertyChange(prop, value) {
    const newSnapshot = _.extend({}, snapshot, { [prop]: value });
    if (prop === "type") {
      if (isNetworkDynProp(newSnapshot) && !hasNetworkEventEditPerms()) {
        alert.error(
          "You do not have permissions to create Network-specific dynamic properties."
        );
        return;
      }
      if (value === "js_code" && !hasJsDynPropEditPerms()) {
        alert.error(
          "You do not have permissions to create JavaScript dynamic properties."
        );
        return;
      }
    }
    onChange(index, newSnapshot);
  }

  // allowEdit - if editing allowed on current snapshot, based on user types, perms.
  function allowEdit(snapshot) {
    if (isNetworkDynProp(snapshot)) {
      return hasNetworkEventEditPerms();
    }
    if (snapshot.type === "js_code") {
      return hasJsDynPropEditPerms();
    }
    return true;
  }

  return (
    <>
      <Grid container spacing={1} className={classes.snapshot}>
        <Grid item xs={3}>
          <MemoTextField
            fullWidth
            autoComplete="off"
            label="Property Name"
            name="dynamic-property-name"
            onChange={(e) => handlePropertyChange("name", e.target.value)}
            InputLabelProps={{ shrink: true }}
            value={snapshot.name}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          />
        </Grid>
        <Grid item xs={3}>
          <MemoTextField
            fullWidth
            label="Type"
            name="type"
            onChange={(e) => handlePropertyChange("type", e.target.value)}
            select
            SelectProps={native}
            value={snapshot.type}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          >
            <option value="css_selector">CSS Selector</option>
            <option value="js_code">JavaScript</option>
            {eventType === "network" && (
              <>
                <option value="network">Network</option>
              </>
            )}
          </MemoTextField>
        </Grid>
        <Grid item xs style={{ position: "relative" }}>
          <AutocompletePropField
            propName={snapshot.prop}
            fullWidth
            label="Value"
            name="value"
            onChange={(value) => handlePropertyChange("value", value)}
            value={snapshot.value}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            disabled={!allowEdit(snapshot)}
          />
          <Box
            component="button"
            style={{
              position: "absolute",
              right: 16,
              top: "50%",
              transform: "translateY(-50%)",
              cursor: "pointer",
            }}
          >
            <EditIcon onClick={() => setCodeEditorModalOpen(true)} />
          </Box>
        </Grid>

        <Grid className={clsx(classes.closeIcon)} item xs={1}>
          <CloseIcon onClick={() => onDelete(index)} />
        </Grid>
      </Grid>
      {codeEditorModalOpen && (
        <CodeEditorModal
          open={codeEditorModalOpen}
          onCancel={handleCodeEditorModalCancel}
          onSave={handleCodeEditorModalSave}
          initialCode={snapshot.value}
          textPrompt={codeEditorPrompt}
          saveEnabled={allowEdit(snapshot)}
          language={languages.js}
        />
      )}
    </>
  );
});

Snapshot.propTypes = {
  eventType: PropTypes.string.isRequired,
  snapshot: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  isNetworkDynProp: PropTypes.func.isRequired,
  hasNetworkEventEditPerms: PropTypes.func.isRequired,
  hasJsDynPropEditPerms: PropTypes.func.isRequired,
};

function getSyncStatusString(syncStatus) {
  if (!syncStatus.last_completed_at) {
    return "Awaiting Initial Sync";
  }

  const completedAt = new Date(syncStatus.last_completed_at).toLocaleString();
  if (syncStatus.last_error_message) {
    return (
      "Last sync failed at " +
      completedAt +
      ": " +
      syncStatus.last_error_message
    );
  }

  const numEvents = syncStatus.last_num_events;
  const resultString =
    numEvents + " event" + (numEvents === 1 ? "" : "s") + " synced";
  return "Last sync: " + resultString + " at " + completedAt;
}

const useEmptyStateClasses = makeStyles(() => ({
  container: {
    paddingTop: 150,
    paddingLeft: 180,
    textAlign: "center",
    display: "flex",
    alignItems: "center",
    flexDirection: "column",
  },
  paragraph: {
    width: 540,
    margin: 20,
  },
}));

// This shows what to render when no events have been created yet.
function EmptyState({ onNewEventDef }) {
  const classes = useEmptyStateClasses();

  return (
    <div className={clsx(classes.container)}>
      <img
        className="mb-4"
        alt="Man creating event schemas"
        src={eventsEmptyStateImage}
        height={201}
        width={273}
      />
      <Typography component="h1" gutterBottom variant="h3">
        You haven&apos;t created any events yet
      </Typography>
      <Typography component="p" className={classes.paragraph}>
        If you&apos;ve installed the Autotrack snippet, Freshpaint is already
        collecting every interaction. Create events in Freshpaint to describe
        certain behaviors on your site or app, and send autotracked data to your
        Destinations.
      </Typography>
      <Button color="primary" onClick={onNewEventDef} variant="contained">
        <AddIcon className={classes.icon} />
        Create New Event
      </Button>
    </div>
  );
}

EmptyState.propTypes = {
  onNewEventDef: PropTypes.func,
};
